import { z } from 'zod'

/**
 * The OAuth consent contract, mirrored by hand from the backend.
 *
 * Source of truth: `internal/http/api/oauthconsent.go`, which is the wire
 * shape, not the service structs behind it (the HTTP layer renames fields).
 * The vocabulary below mirrors `internal/oauthscope/scope.go`.
 *
 * Two deliberate omissions, both security properties rather than oversights:
 *
 *   - `logo_uri` is NOT in the client schema. The backend stores it and never
 *     fetches it; the screen must never render it. Fetching a client-supplied
 *     URL reopens the SSRF surface the CIMD fetcher is fenced against, and
 *     rendering it in the browser hands the client operator the IP address of
 *     every customer at the exact moment they consent. Leaving the field out
 *     of the schema means a backend that starts sending it still cannot get it
 *     onto the page: Zod strips unknown keys on parse.
 *   - `trust_level` is display only, and degrades to "unverified" rather than
 *     failing when it carries a value this build does not know. Nothing in the
 *     consent flow may branch on it, the warning banner least of all: a banner
 *     that swaps for a reassurance on a field value has its off switch in a
 *     database column.
 */

/**
 * The whole scope vocabulary, in the order the consent screen groups it.
 *
 * Kept as data rather than baked into a Zod enum on the wire schemas: a scope
 * added by a backend deploy must not make every consent ticket unparseable
 * until the frontend catches up. Unknown scopes are rendered as unknown, with
 * no description and the highest risk badge, which is the honest thing to show
 * a customer being asked to approve something we cannot explain.
 */
export const OAUTH_SCOPES = [
  'account:read',
  'account:write',
  'account:credentials',
  'billing:read',
  'billing:write',
  'billing:purchase',
  'dns:read',
  'dns:write',
  'dns:credentials',
  'domain:read',
  'domain:write',
  'domain:delete',
  'domain:authcode',
  'domain:transfer',
  'domain:purchase',
  'handle:read',
  'handle:write',
  'handle:delete',
  'hosting:read',
  'hosting:write',
  'hosting:delete',
  'hosting:credentials',
  'hosting:exec',
  'hosting:purchase',
  'k8s:read',
  'k8s:write',
  'k8s:credentials',
  'k8s:purchase',
  'mail:read',
  'mail:write',
  'mail:delete',
  'mail:credentials',
  'mail:purchase',
  'registry:read',
  'registry:write',
  'registry:delete',
  'registry:credentials',
  'registry:purchase',
  'storage:read',
  'storage:write',
  'storage:delete',
  'storage:credentials',
  'storage:purchase',
  'vserver:read',
  'vserver:write',
  'vserver:delete',
  'vserver:credentials',
  'vserver:console',
  'vserver:purchase'
] as const

export type OauthScope = (typeof OAUTH_SCOPES)[number]

const KNOWN_SCOPES: ReadonlySet<string> = new Set(OAUTH_SCOPES)

/** Whether this build knows how to describe `scope` to a customer. */
export const isKnownOauthScope = (scope: string): scope is OauthScope => KNOWN_SCOPES.has(scope)

/** Display-only label. See the file header: nothing may branch on it. */
export const OauthTrustLevel = z.enum(['unverified', 'verified', 'first_party']).catch('unverified')

/** How the client row came to exist. A fact about provenance, not about trust. */
export const OauthClientRegistration = z.enum(['first_party', 'dynamic', 'cimd']).catch('dynamic')

export const OauthConsentClient = z.object({
  client_id: z.string(),
  client_name: z.string(),
  /**
   * The host the backend screened the `client_id` against: for a client
   * identified by a metadata document, the host that document was fetched
   * from. Absent for a dynamically registered client, which has no verified
   * host at all. That absence is not a formatting problem to paper over, it is
   * the thing the customer needs to be told.
   */
  host: z.string().nullish(),
  trust_level: OauthTrustLevel,
  registration: OauthClientRegistration,
  /**
   * Self-asserted by the client. Carried because the backend sends it, and
   * never rendered as an href: a link the client chose, on the page where a
   * customer decides whether to trust it, is the client's own copy in our
   * voice.
   */
  client_uri: z.string().nullish(),
  policy_uri: z.string().nullish(),
  tos_uri: z.string().nullish()
})

/**
 * Why a requested scope was taken off the ticket. An unknown reason parses as
 * `unknown` rather than failing the ticket, and is never explained as an AVV
 * opt-out: that note states a fact about the account.
 */
export const OauthWithheldReason = z.enum(['avv_opted_out', 'unknown']).catch('unknown')

/** A requested scope the account cannot hold, already removed from `scopes_requested`. */
export const OauthWithheldScope = z.object({
  scope: z.string(),
  reason: OauthWithheldReason
})

export const OauthConsentTicket = z.object({
  request_id: z.string(),
  /** The wording this customer was shown, echoed back with their answer. */
  consent_version: z.string(),
  /** RFC 3339. The parked request dies on its own after ten minutes. */
  expires_at: z.string(),
  /** Shown verbatim. Never shortened, prettified or turned into a link. */
  redirect_uri: z.string(),
  // Both tolerate a JSON null: an empty Go slice marshals to null, and a
  // consent screen that fails to parse is a customer who cannot connect
  // anything.
  scopes_requested: z
    .array(z.string())
    .nullish()
    .transform((v) => v ?? []),
  resources: z
    .array(z.string())
    .nullish()
    .transform((v) => v ?? []),
  // Omitted by the backend when nothing is withheld.
  scopes_withheld: z
    .array(OauthWithheldScope)
    .nullish()
    .transform((v) => v ?? []),
  client: OauthConsentClient
})

/**
 * The customer's answer.
 *
 * `consent_version` is deliberately absent: the backend stamps its own onto the
 * grant and never reads one from the body. It is the field whose whole purpose
 * is to be evidence of what the customer was shown, so letting the answering
 * party choose it would make it evidence of nothing.
 */
export const OauthConsentDecision = z.object({
  request_id: z.string(),
  approve: z.boolean(),
  /**
   * Always a subset of `scopes_requested`. A consent screen can only ever take
   * authority away, and an empty array on an approve is a denial on the
   * backend too, so the deny path sends one as well.
   */
  scopes: z.array(z.string()),
  /** RFC 3339, or null for a grant with no expiry. */
  expires_at: z.string().nullable(),
  spend_cap_cents: z.number().int().min(0),
  /**
   * The separate Art. 49(1)(a) GDPR consent to third-country transfers. The
   * backend refuses an approve without it for any client that is not our own,
   * and records it on the grant.
   */
  transfer_consent: z.boolean()
})

export const OauthConsentResponse = z.object({
  /** The client's own redirect URI, already built by the backend. */
  redirect_url: z.string()
})

/**
 * The schemes the browser may be sent to when the decision is done.
 *
 * The backend admits only https, plus http on a loopback host for the native
 * clients that need it, and it builds this URL from the redirect URI it matched
 * at /authorize. This pins the same policy on our side, because this is the one
 * sink on the consent screen where a backend regression would become script
 * execution on our own origin with the session token in localStorage.
 */
export const isAllowedRedirectUrl = (raw: string): boolean => {
  let parsed: URL
  try {
    parsed = new URL(raw)
  } catch {
    return false
  }
  if (parsed.protocol === 'https:') return true
  return (
    parsed.protocol === 'http:' &&
    ['127.0.0.1', '[::1]', '::1', 'localhost'].includes(parsed.hostname)
  )
}

export type OauthTrustLevel = z.infer<typeof OauthTrustLevel>
export type OauthClientRegistration = z.infer<typeof OauthClientRegistration>
export type OauthConsentClient = z.infer<typeof OauthConsentClient>
export type OauthWithheldScope = z.infer<typeof OauthWithheldScope>
export type OauthConsentTicket = z.infer<typeof OauthConsentTicket>
export type OauthConsentDecision = z.infer<typeof OauthConsentDecision>
export type OauthConsentResponse = z.infer<typeof OauthConsentResponse>
